import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {
  JaarrapportageEntity,
  ClientEntity,
  Accountantsverklaring, ToelichtendeParagraaf, BenadrukkingVanAangelegenheden, VerantwoordelijkheidAccountant,
  VerantwoordelijkheidBestuur, HandtekeningAccountant
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzAlgDataService, BokkzJrDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  takeUntil
} from "rxjs";
import {Event, NavigationEnd, Router} from "@angular/router";
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import {CommonModule} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-samenstellingsverklaring',
  templateUrl: './samenstellingsverklaring.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiButtonModule,
    CommonModule
  ],
  styleUrls: ['./samenstellingsverklaring.component.scss']
})
export class SamenstellingsverklaringComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private selectedTabSubject = new ReplaySubject<number>(1);
  public showCustomControls = true;
  private destroy$ = new Subject<void>();
  private routerSubscription: Subscription | undefined;
  private entitySubscription: Subscription | undefined;
  private isSaving = false;
  public index = 0;
  public dialogVisible = false;
  public clientEntity = {} as ClientEntity;
  public jrEntity = {} as JaarrapportageEntity;
  public focusedElementId: string | null = null;
  public isRechtspersoon = false;
  public heeftAandelen = false;
  public selectedTab = 0;

  //informatie
  public objectOfExaminationNatureDescriptionFg = {} as FormGroup;
  public objectOfExaminationPeriodStartDateFg = {} as FormGroup;
  public objectOfExaminationPeriodEndDateFg = {} as FormGroup;
  public objectOfExaminationResponsibleOrganisationDescriptionFg = {} as FormGroup;
  public engagementTypeFg = {} as FormGroup;
  public internalReferenceFg = {} as FormGroup;
  public entityIdentificationTypeFg = {} as FormGroup;
  public entityIdentificationFg = {} as FormGroup;
  public softwarePackageNameFg = {} as FormGroup;
  public softwarePackageVersionFg = {} as FormGroup;

  //inhoud
  public auditorsReportHeaderFg = {} as FormGroup;
  public addresseeFg = {} as FormGroup;
  public introductoryParagraphFg = {} as FormGroup;
  public responsibilityTitleFg = {} as FormGroup;
  public managementsResponsibilityFg = {} as FormGroup;
  public professionalAccountantsResponsibilityFg = {} as FormGroup;

  public reportingTitleFg = {} as FormGroup;
  public emphasisOfMatterUncertaintyAboutGoingConcernFg = {} as FormGroup;
  public emphasisOfMatterFg = {} as FormGroup;
  public otherMatterFg = {} as FormGroup;

  //ondertekening accountant
  public handtekeningAccountantsFg = {} as FormGroup

  constructor(
    public algDataService: BokkzAlgDataService,
    public jrDataService: BokkzJrDataService,
    private router: Router,
    private fb: FormBuilder) {

    //informatie
    this.objectOfExaminationNatureDescriptionFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.objectOfExaminationPeriodStartDateFg = this.jrDataService.createFormGroupCustomDate();
    this.objectOfExaminationPeriodEndDateFg = this.jrDataService.createFormGroupCustomDate();
    this.objectOfExaminationResponsibleOrganisationDescriptionFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.engagementTypeFg = this.fb.group({
      value: 'SAMENSTELLEN'
    })
    this.internalReferenceFg = this.jrDataService.createFormGroupString();
    this.entityIdentificationTypeFg = this.fb.group({
      value: 'KVK'
    })
    this.entityIdentificationFg = this.jrDataService.createFormGroupString();
    this.softwarePackageNameFg = this.fb.group({
      value: 'SnelStart Accountant Pro'
    });
    this.softwarePackageVersionFg = this.fb.group({
      value: '1.0'
    });

    //inhoud
    this.auditorsReportHeaderFg = this.jrDataService.createFormGroupString();
    this.addresseeFg = this.jrDataService.createFormGroupString();
    this.introductoryParagraphFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.responsibilityTitleFg = this.jrDataService.createFormGroupString();
    this.managementsResponsibilityFg = this.fb.group({
      managementsResponsibilities: this.fb.array<VerantwoordelijkheidBestuur>([]),
    })
    this.professionalAccountantsResponsibilityFg = this.fb.group({
      professionalAccountantsResponsibilities: this.fb.array<VerantwoordelijkheidAccountant>([]),
    })
    this.reportingTitleFg = this.jrDataService.createFormGroupString();
    this.emphasisOfMatterUncertaintyAboutGoingConcernFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.emphasisOfMatterFg = this.fb.group({
      emphasisOfMatters: this.fb.array<BenadrukkingVanAangelegenheden>([]),
    })
    this.otherMatterFg = this.fb.group({
      otherMatters: this.fb.array<ToelichtendeParagraaf>([]),
    })

    //ondertekening door accountant
    this.handtekeningAccountantsFg = this.fb.group({
      handtekeningAccountants: this.fb.array<HandtekeningAccountant>([]),
    })
  }

  get managementsResponsibilities(): FormArray {
    return this.managementsResponsibilityFg.get('managementsResponsibilities') as FormArray;
  }

  get professionalAccountantsResponsibilities(): FormArray {
    return this.professionalAccountantsResponsibilityFg.get('professionalAccountantsResponsibilities') as FormArray;
  }

  get emphasisOfMatters(): FormArray {
    return this.emphasisOfMatterFg.get('emphasisOfMatters') as FormArray;
  }

  get otherMatters(): FormArray {
    return this.otherMatterFg.get('otherMatters') as FormArray;
  }

  get handtekeningAccountants(): FormArray {
    return this.handtekeningAccountantsFg.get('handtekeningAccountants') as FormArray;
  }

  addRijManagementsResponsibilities(): void {
    this.managementsResponsibilities.push(this.createFormGroupManagementsResponsibility());
  }

  verwijderRijManagementsResponsibilities(index: number): void {
    this.managementsResponsibilities.removeAt(index);
    this.saveForm();
  }

  addRijProfessionalAccountantsResponsibilities(): void {
    this.professionalAccountantsResponsibilities.push(this.createFormGroupProfessionalAccountantsResponsibility());
  }

  verwijderRijProfessionalAccountantsResponsibilities(index: number): void {
    this.professionalAccountantsResponsibilities.removeAt(index);
    this.saveForm();
  }

  addRijEmphasisOfMatters(): void {
    this.emphasisOfMatters.push(this.createFormGroupEmphasisOfMatter());
  }

  verwijderRijEmphasisOfMatters(index: number): void {
    this.emphasisOfMatters.removeAt(index);
    this.saveForm();
  }

  addRijOtherMatters(): void {
    this.otherMatters.push(this.createFormGroupOtherMatter());
  }

  verwijderRijOtherMatters(index: number): void {
    this.otherMatters.removeAt(index);
    this.saveForm();
  }

  addRijHandtekeningAccountants(): void {
    this.handtekeningAccountants.push(this.createFormGroupHandtekeningAccountant());
  }

  verwijderRijHandtekeningAccountants(index: number): void {
    this.handtekeningAccountants.removeAt(index);
    this.saveForm();
  }

  ngOnInit(): void {

    this.algDataService.client.subscribe((value) => {
      this.clientEntity = value;
    });
    this.jrDataService.isRechtspersoon.subscribe((value) => {
      this.isRechtspersoon = value;
    })
    this.jrDataService.heeftAandelen.subscribe((value) => {
      this.heeftAandelen = value;
    });
    if (this.selectedTabObservable) {
      this.selectedTabObservable
        .subscribe(tab => {
          this.selectedTabSubject.next(tab);
        });
    }

    combineLatest([
      this.selectedTabSubject.pipe(distinctUntilChanged()),
      this.jrDataService.jaarrapportageEntity
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tab, jrEntity]) => {
        this.selectedTab = tab;
        this.jrEntity = jrEntity;
        this.patchData(jrEntity);
      });

    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleUrlChange(event.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
    }
    if (this.entitySubscription) {
      this.entitySubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
    }
  }

  private patchData(value: JaarrapportageEntity) {
    const informatieAccountantsverklaring = value.accountantsverklaring?.informatieAccountantsverklaring;
    const inhoudAccountantsverklaring = value.accountantsverklaring?.inhoudAccountantsverklaring;

    if (informatieAccountantsverklaring) {
      const  informatieObjectVanOnderzoek = informatieAccountantsverklaring.informatieObjectVanOnderzoek;
      if (informatieObjectVanOnderzoek) {
        if (informatieObjectVanOnderzoek.objectOfExaminationNatureDescription) {
          this.objectOfExaminationNatureDescriptionFg.patchValue(informatieObjectVanOnderzoek.objectOfExaminationNatureDescription);
        }
        if (informatieObjectVanOnderzoek.objectOfExaminationPeriodStartDate) {
          this.objectOfExaminationPeriodStartDateFg.patchValue(informatieObjectVanOnderzoek.objectOfExaminationPeriodStartDate);
        }
        if (informatieObjectVanOnderzoek.objectOfExaminationPeriodEndDate) {
          this.objectOfExaminationPeriodEndDateFg.patchValue(informatieObjectVanOnderzoek.objectOfExaminationPeriodEndDate);
        }
        if (informatieObjectVanOnderzoek.objectOfExaminationResponsibleOrganisationDescription) {
          this.objectOfExaminationResponsibleOrganisationDescriptionFg.patchValue(informatieObjectVanOnderzoek.objectOfExaminationResponsibleOrganisationDescription);
        }
      }

      if (informatieAccountantsverklaring.entityIdentification) {
        this.entityIdentificationFg.patchValue(informatieAccountantsverklaring.entityIdentification);
      }
      if (informatieAccountantsverklaring.internalReference) {
        this.internalReferenceFg.patchValue(informatieAccountantsverklaring.internalReference);
      }
    }
    if (inhoudAccountantsverklaring) {
      if (inhoudAccountantsverklaring.auditorsReportHeader) {
        this.auditorsReportHeaderFg.patchValue(inhoudAccountantsverklaring.auditorsReportHeader);
      }
      if (inhoudAccountantsverklaring.addressee) {
        this.addresseeFg.patchValue(inhoudAccountantsverklaring.addressee);
      }
      if (inhoudAccountantsverklaring.introductoryParagraph) {
        this.introductoryParagraphFg.patchValue(inhoudAccountantsverklaring.introductoryParagraph);
      }
      if (inhoudAccountantsverklaring.responsibilityTitle) {
        this.responsibilityTitleFg.patchValue(inhoudAccountantsverklaring.responsibilityTitle);
      }

      this.updateFormManagementsResponsibilities();
      this.updateFormProfessionalAccountantsResponsibilities();

      if (inhoudAccountantsverklaring.reportingTitle) {
        this.reportingTitleFg.patchValue(inhoudAccountantsverklaring.reportingTitle);
      }
      if (inhoudAccountantsverklaring.emphasisOfMatterUncertaintyAboutGoingConcern) {
        this.emphasisOfMatterUncertaintyAboutGoingConcernFg.patchValue(inhoudAccountantsverklaring.emphasisOfMatterUncertaintyAboutGoingConcern);
      }

      this.updateFormEmphasisOfMatters();
      this.updateFormOtherMatters();
      this.updateFormHandtekeningAccountants();
    }
  }

  createFormGroupManagementsResponsibility(item?: VerantwoordelijkheidBestuur): FormGroup {
    return this.fb.group({
      managementsResponsibility: this.fb.group({
        value: item?.managementsResponsibility?.value || null as unknown as string,
      })
    })
  }

  createFormGroupProfessionalAccountantsResponsibility(item?: VerantwoordelijkheidAccountant): FormGroup {
    return this.fb.group({
      professionalAccountantsResponsibility: this.fb.group({
        value: item?.professionalAccountantsResponsibility?.value || null as unknown as string,
      })
    })
  }

  createFormGroupEmphasisOfMatter(item?: BenadrukkingVanAangelegenheden): FormGroup {
    return this.fb.group({
      emphasisOfMatter: this.fb.group({
        value: item?.emphasisOfMatter?.value || null as unknown as string,
      })
    })
  }

  createFormGroupOtherMatter(item?: ToelichtendeParagraaf): FormGroup {
    return this.fb.group({
      otherMatter: this.fb.group({
        value: item?.otherMatter?.value || null as unknown as string,
      })
    })
  }

  createFormGroupHandtekeningAccountant(item?: HandtekeningAccountant): FormGroup {
    return this.fb.group({
      professionalAccountantName: this.fb.group({
        value: item ? item.professionalAccountantName?.value : null as unknown as string,
      }),
      professionalAccountantAddress: this.fb.group({
        value: item ? item.professionalAccountantAddress?.value : null as unknown as string,
      }),
      dateOfReport: this.fb.group({
        value: item ? item.dateOfReport?.value : null as unknown as Date,
      }),
      firmName: this.fb.group({
        value: item ? item.firmName?.value : null as unknown as string,
      }),
    })
  }

  private updateFormManagementsResponsibilities(): void {
    const lijst = this.jrEntity.accountantsverklaring?.inhoudAccountantsverklaring?.verantwoordelijkheidBestuurSpecificatie ?? [];
    const formArray = this.managementsResponsibilityFg.get('managementsResponsibilities') as FormArray;

    this.syncFormArray(formArray, lijst, (item) => this.createFormGroupManagementsResponsibility(item), this.focusedElementId);
  }

  private updateFormProfessionalAccountantsResponsibilities(): void {
    const lijst = this.jrEntity.accountantsverklaring?.inhoudAccountantsverklaring?.verantwoordelijkheidAccountantSpecificatie ?? [];
    const formArray = this.professionalAccountantsResponsibilityFg.get('professionalAccountantsResponsibilities') as FormArray;

    this.syncFormArray(formArray, lijst, (item) => this.createFormGroupProfessionalAccountantsResponsibility(item), this.focusedElementId);
  }

  private updateFormEmphasisOfMatters(): void {
    const lijst = this.jrEntity.accountantsverklaring?.inhoudAccountantsverklaring?.benadrukkingVanAangelegenhedenSpecificatie ?? [];
    const formArray = this.emphasisOfMatterFg.get('emphasisOfMatters') as FormArray;

    this.syncFormArray(formArray, lijst, (item) => this.createFormGroupEmphasisOfMatter(item), this.focusedElementId);
  }

  private updateFormOtherMatters(): void {
    const lijst = this.jrEntity.accountantsverklaring?.inhoudAccountantsverklaring?.toelichtendeParagraafSpecificatie ?? [];
    const formArray = this.otherMatterFg.get('otherMatters') as FormArray;

    this.syncFormArray(formArray, lijst, (item) => this.createFormGroupOtherMatter(item), this.focusedElementId);
  }

  private updateFormHandtekeningAccountants(): void {
    const lijst = this.jrEntity.accountantsverklaring?.inhoudAccountantsverklaring?.handtekeningAccountantSpecificatie ?? [];
    const formArray = this.handtekeningAccountantsFg.get('handtekeningAccountants') as FormArray;

    this.syncFormArray(formArray, lijst, (item) => this.createFormGroupHandtekeningAccountant(item), this.focusedElementId);
  }

  saveForm(): void {
    this.objectOfExaminationNatureDescriptionFg.updateValueAndValidity();
    this.objectOfExaminationPeriodStartDateFg.updateValueAndValidity();
    this.objectOfExaminationPeriodEndDateFg.updateValueAndValidity();
    this.objectOfExaminationResponsibleOrganisationDescriptionFg.updateValueAndValidity();
    this.engagementTypeFg.updateValueAndValidity();
    this.internalReferenceFg.updateValueAndValidity();
    this.entityIdentificationTypeFg.updateValueAndValidity();
    this.entityIdentificationFg.updateValueAndValidity();
    this.softwarePackageNameFg.updateValueAndValidity();
    this.softwarePackageVersionFg.updateValueAndValidity();

    this.auditorsReportHeaderFg.updateValueAndValidity();
    this.addresseeFg.updateValueAndValidity();
    this.introductoryParagraphFg.updateValueAndValidity();
    this.responsibilityTitleFg.updateValueAndValidity();
    this.managementsResponsibilityFg.updateValueAndValidity();
    this.professionalAccountantsResponsibilityFg.updateValueAndValidity();
    this.reportingTitleFg.updateValueAndValidity();
    this.emphasisOfMatterUncertaintyAboutGoingConcernFg.updateValueAndValidity();
    this.emphasisOfMatterFg.updateValueAndValidity();
    this.otherMatterFg.updateValueAndValidity();

    this.handtekeningAccountantsFg.updateValueAndValidity();

    if (this.jrEntity && this.jrEntity.accountantsverklaring) {
      this.jrEntity = {
        ...this.jrEntity,
        accountantsverklaring: {
          ...this.jrEntity.accountantsverklaring,
          informatieAccountantsverklaring: {
            ...this.jrEntity.accountantsverklaring.informatieAccountantsverklaring,
            informatieObjectVanOnderzoek: {
              ...this.jrEntity.accountantsverklaring.informatieAccountantsverklaring?.informatieObjectVanOnderzoek,
              objectOfExaminationNatureDescription: this.objectOfExaminationNatureDescriptionFg.value,
              objectOfExaminationPeriodStartDate: this.objectOfExaminationPeriodStartDateFg.value,
              objectOfExaminationPeriodEndDate: this.objectOfExaminationPeriodEndDateFg.value,
              objectOfExaminationResponsibleOrganisationDescription: this.objectOfExaminationResponsibleOrganisationDescriptionFg.value,
            },
            engagementType: this.engagementTypeFg.getRawValue(),
            internalReference: this.internalReferenceFg.value,
            entityIdentificationType: this.entityIdentificationTypeFg.getRawValue(),
            entityIdentification: this.entityIdentificationFg.value,
            softwarePackageName: this.softwarePackageNameFg.value,
            softwarePackageVersion: this.softwarePackageVersionFg.value,
          },
          inhoudAccountantsverklaring: {
            ...this.jrEntity.accountantsverklaring.inhoudAccountantsverklaring,
            auditorsReportHeader: this.auditorsReportHeaderFg.value,
            addressee: this.addresseeFg.value,
            introductoryParagraph: this.introductoryParagraphFg.value,
            responsibilityTitle: this.responsibilityTitleFg.value,
            verantwoordelijkheidBestuurSpecificatie: this.managementsResponsibilityFg.getRawValue().managementsResponsibilities,
            verantwoordelijkheidAccountantSpecificatie: this.professionalAccountantsResponsibilityFg.getRawValue().professionalAccountantsResponsibilities,
            reportingTitle: this.reportingTitleFg.value,
            emphasisOfMatterUncertaintyAboutGoingConcern: this.emphasisOfMatterUncertaintyAboutGoingConcernFg.value,
            benadrukkingVanAangelegenhedenSpecificatie: this.emphasisOfMatterFg.getRawValue().emphasisOfMatters,
            toelichtendeParagraafSpecificatie: this.otherMatterFg.getRawValue().otherMatters,
            handtekeningAccountantSpecificatie: this.handtekeningAccountantsFg.getRawValue().handtekeningAccountants,
          }
        }
      } as JaarrapportageEntity

      this.jrDataService.updateEntity(this.jrEntity);
    }
  }

  handleUrlChange(newUrl: string) {
    if (!this.isSaving) {
      this.isSaving = true;
      this.jrDataService.updateEntity(this.jrEntity);
      this.isSaving = false;
    }
  }

  //generieke synchronisatie-helper
  private syncFormArray<T extends { [key: string]: any }>(
    formArray: FormArray,
    data: T[],
    createGroup: (item: T) => FormGroup,
    focusedElementId?: string | null
  ): void {

    //verwijder overbodige controls
    while (formArray.length > data.length) {
      formArray.removeAt(formArray.length - 1);
    }

    //voeg ontbrekende controls toe
    while (formArray.length < data.length) {
      const index = formArray.length;
      formArray.push(createGroup(data[index]));
    }

    //patch controls
    for (let i = 0; i < data.length; i++) {
      const control = formArray.at(i) as FormGroup;
      control.patchValue(data[i]);
    }

    //focus
    if (focusedElementId) {
      document.getElementById(focusedElementId)?.focus();
    }
  }
}
